/** One row in the agent review queue — uses Mission Control card chrome. */

import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboardCheck } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { MissionControlCard } from "@/components/mission-control/MissionControlCard";
import { Badge } from "@/components/ui/badge/Badge";
import { formatRelativeTime } from "@/components/missions/MissionRow";

export interface ReviewQueueRowProps {
  caseLabel: string;
  subjectLabel?: string | null;
  summarySnippet?: string | null;
  taskName: string;
  occurredAt?: string | null;
  stepCount?: number;
  reviewed: boolean;
  infrastructureFailure?: boolean;
  /** Compact queue layout — case info only; meta/status rendered by the parent grid. */
  variant?: "card" | "queue";
  /** When true, row click toggles selection instead of opening the case. */
  selectionMode?: boolean;
  selected?: boolean;
  onOpen: () => void;
  onToggleSelect?: () => void;
}

function queueMetaParts({
  occurredAt,
  stepCount,
  caseLabel,
  subjectLabel,
}: Pick<ReviewQueueRowProps, "occurredAt" | "stepCount" | "caseLabel" | "subjectLabel">) {
  const when = formatRelativeTime(occurredAt);
  return [
    when,
    stepCount && stepCount > 0 ? `${stepCount} steps` : null,
    subjectLabel?.trim() ? caseLabel : null,
  ].filter(Boolean);
}

export function ReviewQueueRowMeta({
  occurredAt,
  stepCount,
  caseLabel,
  subjectLabel,
}: Pick<ReviewQueueRowProps, "occurredAt" | "stepCount" | "caseLabel" | "subjectLabel">) {
  const parts = queueMetaParts({ occurredAt, stepCount, caseLabel, subjectLabel });
  if (parts.length === 0) return null;
  return (
    <span className="whitespace-nowrap text-xs text-slate-400 tabular-nums">{parts.join(" · ")}</span>
  );
}

export function ReviewQueueRowStatus({
  reviewed,
  infrastructureFailure = false,
}: Pick<ReviewQueueRowProps, "reviewed" | "infrastructureFailure">) {
  if (infrastructureFailure) {
    return (
      <Badge color="rose" size="sm" className="!normal-case !tracking-normal !font-sans">
        Infra failure
      </Badge>
    );
  }
  if (reviewed) {
    return (
      <Badge color="emerald" size="sm" className="!normal-case !tracking-normal !font-sans">
        Reviewed
      </Badge>
    );
  }
  return null;
}

export function ReviewQueueRow({
  caseLabel,
  subjectLabel,
  summarySnippet,
  taskName,
  occurredAt,
  stepCount,
  reviewed,
  infrastructureFailure = false,
  variant = "card",
  selectionMode = false,
  selected = false,
  onOpen,
  onToggleSelect,
}: ReviewQueueRowProps) {
  const when = formatRelativeTime(occurredAt);
  const title = subjectLabel?.trim() || caseLabel;
  const metaParts = queueMetaParts({ occurredAt, stepCount, caseLabel, subjectLabel });
  const complete = reviewed || infrastructureFailure;

  const handleClick = () => {
    if (selectionMode && onToggleSelect) {
      onToggleSelect();
      return;
    }
    onOpen();
  };

  if (variant === "queue") {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={clsx(
          "relative w-full min-w-0 rounded-xl px-3 py-2.5 text-left transition-colors",
          selectionMode ? "cursor-pointer" : "hover:bg-white/[0.04] focus:bg-white/[0.04]",
          !selectionMode && "focus:outline-none",
          selected && "bg-indigo-500/10 ring-1 ring-indigo-400/45",
        )}
      >
        {complete ? (
          <div
            aria-hidden
            className={clsx(
              "pointer-events-none absolute inset-y-0 left-0 w-full",
              infrastructureFailure ? "bg-rose-500/[0.04]" : "bg-emerald-500/[0.04]",
            )}
          />
        ) : null}
        <div className="relative z-[1] flex min-w-0 items-start gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-500/15">
            <FontAwesomeIcon icon={faClipboardCheck} className="text-indigo-300 text-sm" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-100">
              {title}
              <span className="font-normal text-slate-400"> — {taskName}</span>
            </p>
            {summarySnippet?.trim() ? (
              <p className="mt-0.5 text-xs text-slate-400">{summarySnippet}</p>
            ) : (
              <p className="mt-0.5 text-xs text-slate-500">
                Two ratings: how it gathered information, then what it concluded
              </p>
            )}
          </div>
        </div>
      </button>
    );
  }

  return (
    <MissionControlCard
      as="button"
      onClick={onOpen}
      icon={
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-500/15">
          <FontAwesomeIcon icon={faClipboardCheck} className="text-indigo-300" />
        </div>
      }
      title={
        <>
          {title}
          <span className="text-slate-400 font-normal"> — {taskName}</span>
        </>
      }
      subtitle={
        summarySnippet?.trim() ? (
          <p className="mt-0.5 text-xs text-slate-400 line-clamp-2">{summarySnippet}</p>
        ) : (
          <p className="mt-0.5 text-xs text-slate-500">
            Two ratings: how it gathered information, then what it concluded
          </p>
        )
      }
      timestamp={metaParts.length > 0 ? metaParts.join(" · ") : when || undefined}
      status={<ReviewQueueRowStatus reviewed={reviewed} infrastructureFailure={infrastructureFailure} />}
      progressPct={complete ? 100 : 0}
      progressComplete={complete}
    />
  );
}
